import type { UseFormReturn } from "react-hook-form";

import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";

import { MetadataMultiSelect } from "@/components/itsm-config/metadata-multi-select";
import { MetadataSelect } from "@/components/itsm-config/metadata-select";
import { TagListInput } from "@/components/itsm-config/tag-list-input";
import { useCatalogItemNames } from "@/hooks/use-catalog-item-names";
import { useCustomFieldDefinitions } from "@/hooks/use-custom-fields";
import type { ConditionValues } from "@/lib/itsm-config/export-rule-conditions";
import type { ExportMappingRule } from "@/lib/types/itsm-config";

// The conditions an export rule can narrow its desk by - catalog items and a
// custom field value - and the "Don't sync" switch, as the rule table and
// dialogs show them. Shared by the ITSM and project-management rule editors.

export function ConditionsCell({
  rule,
  catalogNames,
}: {
  rule: ExportMappingRule;
  catalogNames: Map<string, string>;
}) {
  const items = rule.match.service_catalog_item_ids ?? [];
  const field = rule.match.custom_field;
  if (!items.length && !field) return <>—</>;
  return (
    <div className="space-y-0.5 text-xs">
      {items.length > 0 && (
        <div>
          <span className="text-muted-foreground">Catalog: </span>
          {items.map((id) => catalogNames.get(id) ?? id).join(", ")}
        </div>
      )}
      {field && (
        <div>
          <span className="text-muted-foreground">{field.name} = </span>
          {field.values.join(", ")}
        </div>
      )}
    </div>
  );
}

/**
 * The catalog item, custom field and "Don't sync" inputs of a rule dialog.
 * `form` is the dialog's form, viewed through the fields this component owns.
 */
export function ExportRuleConditionFields({
  form,
  tenantId,
  deskId,
  excludeDescription,
}: {
  form: UseFormReturn<ConditionValues>;
  tenantId: string;
  deskId?: string;
  excludeDescription: string;
}) {
  const catalog = useCatalogItemNames(tenantId);
  const catalogOptions = [...catalog.names]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
  // Desk fields for the chosen desk plus tenant-wide ones. A catalog form
  // field is not a definition, so its label can be typed in.
  const fieldDefinitions = useCustomFieldDefinitions(
    tenantId,
    deskId ? [deskId] : [],
  );
  const fieldOptions = [
    ...new Set(fieldDefinitions.definitions.map((d) => d.name)),
  ].map((name) => ({ value: name, label: name }));

  return (
    <>
      <FormField
        control={form.control}
        name="service_catalog_item_ids"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Service catalog items (optional)</FormLabel>
            <FormControl>
              <MetadataMultiSelect
                options={catalogOptions}
                isLoading={catalog.isLoading}
                value={field.value}
                onChange={field.onChange}
                allowCustom={false}
                placeholder="Any catalog item"
              />
            </FormControl>
            <FormDescription>
              Only tickets opened from one of these items.
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="grid grid-cols-2 gap-3">
        <FormField
          control={form.control}
          name="custom_field_name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Custom field (optional)</FormLabel>
              <FormControl>
                <MetadataSelect
                  options={fieldOptions}
                  isLoading={fieldDefinitions.isLoading}
                  value={field.value ?? ""}
                  onChange={(v) => {
                    field.onChange(v);
                    if (!v) form.setValue("custom_field_values", []);
                  }}
                  placeholder="Any value"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="custom_field_values"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Equals any of</FormLabel>
              <FormControl>
                <TagListInput
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Type a value, Enter"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <FormField
        control={form.control}
        name="exclude"
        render={({ field }) => (
          <FormItem className="flex items-start gap-3 rounded-md border px-4 py-3">
            <div className="flex-1 space-y-0.5">
              <FormLabel>Don&apos;t sync</FormLabel>
              <FormDescription>{excludeDescription}</FormDescription>
              <FormMessage />
            </div>
            <FormControl>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </FormControl>
          </FormItem>
        )}
      />
    </>
  );
}
